import * as _ from 'underscore';
import { util } from '../../../../../shared/util';
import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { BaseDashboardFiltersComponent } from '../base-dashboard-filters.component';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../../../../../shared/formly/ga-formly.service';
import { GaValidateService } from '../../../../../shared/formly/ga-validate.service';
import { GaMessageService } from '../../../../../shared/message/ga-message.service';
import { TranslationService } from '../../../../../shared/translation/translation.service';
import { ReportDataService } from '../../../../../shared/report/report-data.service';
import { ReportFiltersUtil } from '../../../../../shared/report/report-filters-util.service';
import { DashboardService } from '../../dashboard.service';
import { GaDeferredService } from '../../../../../shared/services/ga-deferred.service';
import { AuthService } from '../../../../../shared/services/auth/auth.service';
import { Override } from '../../../../../shared/app-decorators';
import { FormattingService } from '../../../../../shared/services/formatting.service';
import { ICompany, IContact, IDescribedObject, INamedObject } from '../../../../../../type_definitions/datatypes';
import { WebshopDataService } from '../../../../webshop/webshop-data.service';
import { CompanyDataService } from '../../../../../shared/company/company-data.service';
import { DashboardDataService } from '../../../../../shared/report/dashboard-data.service';
import { CustomerDataService } from './customer-data.service';
import { GaUserService } from '../../../../../shared/services/ga-user.service';

@Component({
  selector: 'customer-dashboard-filter',
  templateUrl: '../base-dashboard-filters.component.html'
})
export class CustomerDashboardFiltersComponent extends BaseDashboardFiltersComponent implements OnInit, OnDestroy {
  @Input() public preselectGbdm:boolean;
  @Input() public preselectLamContact:boolean;
  @Input() public preselectLamCompany:boolean;
  @Input() public prefilledWebshopPhaseValue:boolean;
  @Input() public showWebshopPhase:boolean;
  @Input() public showSubCompany:boolean;
  @Input() public showLamContact:boolean;
  @Input() public showTestOrder:boolean;
  @Input() public validatorFn:Function;

  private countryField:IExtraFieldConfigurationObject;
  private webshopPhaseField:IExtraFieldConfigurationObject;
  private customerField:IExtraFieldConfigurationObject;
  private customerCompanyField:IExtraFieldConfigurationObject;
  private lamField:IExtraFieldConfigurationObject;
  private lamContactField:IExtraFieldConfigurationObject;
  private lamCompanyField:IExtraFieldConfigurationObject;
  private gbdmField:IExtraFieldConfigurationObject;
  private lamGroupField:IExtraFieldConfigurationObject;
  private subCompanyField:IExtraFieldConfigurationObject;
  private gbdmDeskField:IExtraFieldConfigurationObject;
  private testOrderField:IExtraFieldConfigurationObject;

  constructor(
    private formlyService:GaFormlyService,
    private formattingService:FormattingService,
    private webshopDataService:WebshopDataService,
    private companyDataService:CompanyDataService,
    private dashboardDataService:DashboardDataService,
    private customerDataService:CustomerDataService,
    private userService:GaUserService,
    validateService:GaValidateService,
    messageService:GaMessageService,
    translationService:TranslationService,
    reportDataService:ReportDataService,
    reportFiltersUtil:ReportFiltersUtil,
    dashboardService:DashboardService,
    deferredService:GaDeferredService,
    authService:AuthService,
    cdr:ChangeDetectorRef
  ) {
    super(validateService, messageService, translationService, reportDataService, reportFiltersUtil, dashboardService, deferredService, authService, cdr);
  }

  @Override(BaseDashboardFiltersComponent)
  public ngOnInit():void {
    super.ngOnInit();
    // default value for test order filter
    this.model.testOrder = 0;
  }

  protected customValidatorFn = ():Promise<void | string[]> => {
    const allLabelsVisibleFields:string[] = _.chain(this.fields)
      .filter((field:IExtraFieldConfigurationObject) => !field.templateOptions.hidden)
      .map((field:IExtraFieldConfigurationObject) => field.templateOptions.label)
      .value();

    const validatorMessage:string = this.validatorFn && this.validatorFn(this.model, allLabelsVisibleFields);

    if (validatorMessage) {
      return Promise.reject(validatorMessage);
    }

    return Promise.resolve();
  };

  @Override(BaseDashboardFiltersComponent)
  public fillFields():void {
    const promises:Promise<any>[] = [
      this.formlyService.fillField(this.customerField, this.webshopDataService.webshopsWithReadOnlyAccess()),
      this.formlyService.fillField(this.customerCompanyField, this.companyDataService.customerCompanies())
        .then((customerCompanies:ICompany[]) => {
          if (this.userService.isCustomerCompany) {
            this.form.patchValue({ customerCompany: _.findWhere(customerCompanies, { id: util.extractId(this.filters[ 'customerCompany' ]) || this.userService.company.id }) });
          }
        }),
      this.formlyService.fillField(this.countryField, this.customerDataService.countries())
    ];

    if (this.webshopPhaseField) {
      if (this.prefilledWebshopPhaseValue) {
        promises.push(
          this.formlyService.fillField(this.webshopPhaseField, this.webshopDataService.webshopPhases())
            .then((webshopPhases:IDescribedObject[]) => {
              this.form.patchValue({ webshopPhase: _.findWhere(webshopPhases, { id: util.extractId(this.filters[ 'webshopPhase' ]) || '@PRESALES@' }) });
            })
        );
      } else {
        promises.push(
          this.formlyService.fillField(this.webshopPhaseField, this.webshopDataService.webshopPhases())
        );
      }
    }

    if (this.lamField) {
      promises.push(
        this.formlyService.fillField(this.lamField, this.dashboardDataService.LAMs())
      );
    }

    if (this.lamContactField) {
      promises.push(
        this.formlyService.fillField(this.lamContactField, this.dashboardDataService.LAMContacts())
          .then((lamContacts:INamedObject[]) => {
            if (this.preselectLamContact) {
              // preselect the current user
              this.form.patchValue({ lamContact: _.findWhere(lamContacts, { id: util.extractId(this.filters[ 'lamContact' ]) || this.currentUser.id }) });
            }
          })
      );
    }

    if (this.lamCompanyField) {
      promises.push(
        this.formlyService.fillField(this.lamCompanyField, this.dashboardDataService.LAMCompanies())
          .then((lamCompanies:ICompany[]) => {
            if (this.preselectLamCompany) {
              // preselect the company of the current user
              this.form.patchValue({ lamCompany: _.findWhere(lamCompanies, { id: util.extractId(this.filters[ 'lamCompany' ]) || this.userService.company.id }) });
            }
          })
      );
    }

    if (this.lamGroupField) {
      promises.push(
        this.formlyService.fillField(this.lamGroupField, this.customerDataService.LAMGroups())
      );
    }

    if (this.gbdmField) {
      promises.push(
        this.formlyService.fillField(this.gbdmField, this.dashboardDataService.gbdms())
          .then((gbdms:IContact[]) => {
            if (this.preselectGbdm) {
              // preselect the current user
              this.form.patchValue({ gbdm: _.findWhere(gbdms, { id: util.extractId(this.filters[ 'gbdm' ]) || this.currentUser.id }) });
            }
          })
      );
    }

    if (this.gbdmDeskField) {
      promises.push(
        this.formlyService.fillField(this.gbdmDeskField, this.dashboardDataService.gbdmDesks())
      );
    }

    if (this.subCompanyField) {
      promises.push(
        this.formlyService.fillField(this.subCompanyField, this.companyDataService.getSubCompaniesForAUser(this.currentUser.id))
      );
    }

    if (this.showTestOrder) {
      promises.push(
        this.formlyService.fillField(this.testOrderField, this.dashboardDataService.yesNo())
      );
    }

    Promise.all(promises)
      .then(this.setQueryFilters)
      .then(() => {
        if (this.model.gbdm && this.autoSearch) {
          this.startSearch();
        }
      });
  }

  @Override(BaseDashboardFiltersComponent)
  public createFields():IExtraFieldConfigurationObject[] {

    const fields:IExtraFieldConfigurationObject[] = [];
    fields.push(
      this.countryField = this.formlyService.aSelect('country', 'GA.GENERAL.COUNTRY')
        .withoutEditable()
        .withWrapperClass(this.wrapperClass)
        .withFormat(this.formattingService.formatCountry)
        .withExtraOptions({
          templateOptions: {
            localSearch: true
          }
        })
        .build()
    );

    fields.push(
      this.customerCompanyField = this.formlyService.aSearch('customerCompany', 'Customer company')
        .withWrapperClass(this.wrapperClass)
        .withFormat(this.formattingService.formatCompany)
        .withExtraOptions({
          templateOptions: {
            localSearch: true
          }
        })
        .build()
    );

    if (this.showWebshopPhase) {
      fields.push(
        this.webshopPhaseField = this.formlyService.aSelect('webshopPhase', 'Webshop phase')
          .withWrapperClass(this.wrapperClass)
          .withFormat(this.formattingService.formatLov)
          .withExtraOptions({
            templateOptions: {
              localSearch: true
            }
          })
          .build()
      );
    }

    fields.push(
      this.customerField = this.formlyService.aSearch('customer', 'GA.GENERAL.CUSTOMER')
        .withoutEditable()
        .withWrapperClass(this.wrapperClass)
        .withFormat(this.formattingService.formatCompany)
        .withExtraOptions({
          templateOptions: {
            localSearch: true
          }
        })
        .build()
    );

    // customers cannot see a couple of filters such as LAM or GBDM filters
    if (!this.userService.isCustomerCompany) {
      fields.push(
        this.lamField = this.formlyService.aSearch('lam', 'LAM')
          .withWrapperClass(this.wrapperClass)
          .withFormat(this.formattingService.formatName)
          .withExtraOptions({
            templateOptions: {
              localSearch: true
            }
          })
          .build()
      );

      if (this.showLamContact) {
        fields.push(
          this.lamContactField = this.formlyService.aSearch('lamContact', 'LAM Contact')
            .withWrapperClass(this.wrapperClass)
            .withFormat(this.formattingService.formatName)
            .withExtraOptions({
              templateOptions: {
                localSearch: true
              }
            })
            .build()
        );
      }

      fields.push(
        this.lamCompanyField = this.formlyService.aSearch('lamCompany', 'LAM Company')
          .withWrapperClass(this.wrapperClass)
          .withFormat(this.formattingService.formatName)
          .withExtraOptions({
            templateOptions: {
              localSearch: true
            }
          })
          .build()
      );

      fields.push(
        this.lamGroupField = this.formlyService.aSearch('lamGroup', 'LAM Group')
          .withWrapperClass(this.wrapperClass)
          .withFormat(this.formattingService.formatName)
          .withExtraOptions({
            templateOptions: {
              localSearch: true
            }
          })
          .build()
      );

      // LAM & Vendor companies cannot see GBDM filters
      if (!this.userService.isPartnerLAMCompany && !this.userService.isVendorCompany) {
        fields.push(
          this.gbdmField = this.formlyService.aSelect('gbdm', 'GBDM')
            .withWrapperClass(this.wrapperClass)
            .withFormat(this.formattingService.formatContact)
            .withExtraOptions({
              templateOptions: {
                localSearch: true
              }
            })
            .build()
        );

        fields.push(
          this.gbdmDeskField = this.formlyService.aSelect('gbdmDesk', 'GBDM Desk')
            .withWrapperClass(this.wrapperClass)
            .withFormat(this.formattingService.formatDepartment)
            .build()
        );
      }
    }

    if (this.showSubCompany) {
      fields.push(
        this.subCompanyField = this.formlyService.aSearch('subCompany', 'Sub company')
          .withWrapperClass(this.wrapperClass)
          .withFormat(this.formattingService.formatCompany)
          .withExtraOptions({
            templateOptions: {
              localSearch: true
            }
          })
          .build()
      );
    }

    if (this.showTestOrder) {
      fields.push(
        this.testOrderField = this.formlyService.aSelect('testOrder', 'Test order')
          .withWrapperClass(this.wrapperClass)
          .withFormat(this.formattingService.formatLov)
          .build()
      );
    }

    return fields;
  }

  public ngOnDestroy():void {
    this.dashboardDataService.clear();
    this.customerDataService.clear();
  }
}